import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { ChevronDown, ChevronRight, X, RotateCcw, Undo2, Send } from 'lucide-react'
import { api } from '../lib/api'
import { trackEvent } from '../lib/analytics'
import { useAuth } from '../context/AuthContext'
import { formatCurrency, formatDateTime, formatScheduledWindow, STATUS_LABELS, STATUS_COLORS } from '../lib/utils'
import CsvControls from '../components/CsvControls'

// Filter options. `returned_to_store` is a derived state (see migration
// 0082 marker-column note) — the worker translates it to the right
// status='cancelled' + returned_to_store_at marker condition.
const STATUSES = ['', 'pending', 'confirmed', 'preparing', 'out_for_delivery', 'delivered', 'cancelled', 'returned_to_store']

// POS void reason labels: imported from the shared modal so the dedicated
// Sales (POS) page + this mixed-feed Orders page share the same labels.
import { POS_VOID_REASON_LABELS as VOID_REASON_LABELS, PosVoidModal } from '../components/PosVoidModal'
import { REFUND_REASON_LABELS, RefundReasonModal } from '../components/RefundReasonModal'
import { SendReceiptModal } from '../components/SendReceiptModal'
import { JofotaraTriageCard, type JofotaraStatus } from '../components/JofotaraTriage'

// Return reason chip set — matches the cashier-side picker in the POS
// app's returns flow and the CHECK on `pos_returns.reason_code`.
const REASON_LABELS: Record<string, string> = {
  damaged: 'Damaged',
  wrong_item: 'Wrong item',
  changed_mind: 'Customer changed mind',
  other: 'Other',
}

interface Order {
  id: string; order_number: string; total_fils: number; delivery_fee_fils: number
  status: string; returned_to_store_at: string | null
  // Channel discriminator (migration 0104): 'delivery' (default) or 'pos'
  // (rung up at the iPad till). POS rows skip the delivery columns and
  // can't be flipped through the status dropdown — voiding goes through a
  // separate admin action (slice 2b).
  channel: 'delivery' | 'pos'
  // Set on delivery orders rung up by a cashier (phone-in flow, v1 2026-06-22).
  // `channel='delivery'` + `cashier_id` is non-null → phone-in. Surfaced as a
  // "📞 Phone-in" badge alongside the channel chip.
  cashier_id?: string | null
  fulfillment: string; delivery_name: string; delivery_phone: string; delivery_address: string
  payment_method: string; payment_status?: string; card_authorization_id?: string | null
  // Derived in the worker: labelled by what zeroed the order. For total=0
  // it's the largest non-zero contributor ('gift_card' | 'store_credit');
  // for partial coverage it's 'store_credit' when the wallet alone covered
  // the full residual, otherwise the underlying tender. Use for display.
  effective_payment_method?: string
  store_credit_applied_fils?: number
  gift_card_applied_fils?: number
  created_at: string; full_name: string; phone: string
  // POS partial-refund marker (migration 0113). When set but the order is
  // not voided / fully refunded, the row renders as "Partial refund" so ops
  // can see at a glance that some lines came back.
  partially_refunded_at?: string | null
  // Scheduled delivery window (migration 0117). NULL = ASAP. When set,
  // the row + drawer show "📅 Mon 7–8pm" so dispatch doesn't send a
  // driver out before the window opens.
  scheduled_for?: string | null
  scheduled_window_minutes?: number | null
}

interface OrderItem {
  quantity: number; unit_price_fils: number
  id: string; name: string; name_ar: string; image_key: string | null
  gift_wrap: number | boolean; gift_note: string | null
}

interface OrderAddon {
  name_en: string; name_ar: string; quantity: number; unit_price: number; total: number
}

interface PrizeItem {
  play_id: string; name_en: string; name_ar: string; prize_type: string; game_name: string
}

interface OrderDetail {
  order: Order & {
    subtotal_fils: number; notes: string | null; user_id: string
    discount_amount_fils: number; discount_code: string | null
    bundle_discount_fils?: number
    tip_fils?: number
    rating?: number | null; rating_comment?: string | null
    payment_status?: string
    card_authorization_id?: string | null
    // POS-specific (channel='pos' rows) — populated by migration 0104+.
    cashier_id?: string | null
    cashier_name?: string | null
    tendered_fils?: number | null
    change_fils?: number | null
    // Scheduled delivery (migration 0117 + phone-in flow). NULL = ASAP.
    scheduled_for?: string | null
    scheduled_window_minutes?: number | null
    drops_earned?: number
    loyalty_points_earned?: number
    store_credit_applied_fils?: number
    gift_card_applied_fils?: number
    gift_card_id?: string | null
    // POS void metadata (slice 2b — populated by `/admin/orders/:id/void-pos`).
    voided_at?: string | null
    voided_by_admin_id?: string | null
    voided_by_admin_email?: string | null
    void_reason_code?: string | null
    void_reason?: string | null
    // Delivery-refund metadata (migration 0133 — populated by
    // POST /admin/orders/:id/refund). Shown on the drawer next to the
    // refunded-payment badge so ops sees the reason without diving into
    // the audit log.
    refund_reason_code?: string | null
    refund_reason?: string | null
    // POS partial-refund marker (migration 0113 — stamped on first partial
    // return; once cumulative refund hits total, payment_status flips to
    // 'refunded' and this stamp stays for the audit trail).
    partially_refunded_at?: string | null
    // Derived in the worker — 'void' or 'refund' if the latest gateway
    // action on this order was either. Used here to differentiate the
    // badge since payment_status alone collapses both to 'refunded'
    // (the CHECK constraint doesn't allow a separate 'voided' value).
    payment_last_action?: 'void' | 'refund' | null
    // Margin fields (added 2026-07-12) — computed server-side from
    // stock_movements' snapshotted unit_cost_fils. margin_fils =
    // subtotal - COGS. margin_pct = null when subtotal is 0 (100%
    // wallet-tender edge case).
    cogs_fils?: number
    margin_fils?: number
    margin_pct?: number | null
    // JoFotara sale + void submissions (populated by the delivered-hook
    // added 2026-07-10 — customer-app and phone-in delivery orders both
    // submit at the `status='delivered'` transition). Both fields are
    // NULL until submission fires. Surfaced via the shared triage card.
    jofotara_status?: string | null
    jofotara_invoice_id?: string | null
    jofotara_uuid?: string | null
    jofotara_submitted_at?: string | null
    jofotara_attempts?: number | null
    jofotara_response_json?: string | null
    jofotara_void_status?: string | null
    jofotara_void_invoice_id?: string | null
    jofotara_void_uuid?: string | null
    jofotara_void_submitted_at?: string | null
    jofotara_void_attempts?: number | null
    jofotara_void_response_json?: string | null
  }
  items: OrderItem[]
  addons: OrderAddon[]
  prizeItems: PrizeItem[]
  // Each pos_returns row issued against this sale (migration 0113).
  // Empty for delivery orders or POS orders with no returns yet.
  returns: PosReturn[]
  // Bundles applied to this order at ring-up. Snapshot names come from
  // `order_bundles.snapshot_json` so they render correctly even after
  // the rule was edited or deactivated. Empty on orders placed before
  // Phase B (2026-07-18) or when no rule matched.
  applied_bundles?: Array<{
    id: string
    slug: string
    kind: 'fixed' | 'bxgy'
    name_en: string
    name_ar: string
    discount_fils: number
  }>
}

interface PosReturnLine {
  return_id: string
  product_name: string | null
  quantity: number
  refund_line_fils: number
  restocked: number   // 0 / 1
}

interface PosReturn {
  id: string
  return_number: string
  total_refund_fils: number
  refund_method: 'cash' | 'store_credit' | 'card'
  reason_code: string | null
  reason: string | null
  created_at: string
  drops_reversed: number
  loyalty_points_reversed: number
  store_credit_reversed_fils: number
  cashier_name: string | null
  authorised_by_admin_email: string | null
  lines: PosReturnLine[]
}

// Small badge for the card payment_status. Three nuances:
//   • Auth held — card_authorization_id is set (auth, not captured yet)
//   • Voided — payment_status='refunded' + last action was a void
//     (in-window cancel; customer never sees a charge)
//   • Refunded — payment_status='refunded' + last action was a refund
//     (post-capture; money moved then came back)
function PaymentStatusBadge({
  status, authPending, lastAction,
}: { status: string; authPending: boolean; lastAction?: 'void' | 'refund' | null }) {
  const label = authPending
    ? 'Auth held'
    : status === 'paid' ? 'Paid'
    : status === 'refunded'
      ? (lastAction === 'void' ? 'Voided' : 'Refunded')
    : status === 'failed' ? 'Failed'
    : status === 'pending' ? 'Pending'
    : status
  const tone = authPending
    ? 'bg-amber-500/15 text-amber-300'
    : status === 'paid' ? 'bg-green-500/15 text-green-400'
    : status === 'refunded' ? 'bg-blue-500/15 text-blue-300'
    : status === 'failed' ? 'bg-red-500/15 text-red-300'
    : 'bg-white/10 text-gray-300'
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${tone}`}>
      {label}
    </span>
  )
}

function OrderDetailPanel({ orderId, onClose }: { orderId: string; onClose: () => void }) {
  const { user } = useAuth()
  const qc = useQueryClient()
  const canRefund = user?.role === 'super_admin' || user?.role === 'operations'
  const [refundError, setRefundError] = useState<string | null>(null)
  const [refundResult, setRefundResult] = useState<{ type: string; amount_fils: number } | null>(null)
  const [refundModalOpen, setRefundModalOpen] = useState(false)
  const [sendReceiptOpen, setSendReceiptOpen] = useState(false)
  const refund = useMutation({
    mutationFn: (payload: { reason_code: string; reason_text?: string }) =>
      api.post(`/orders/${orderId}/refund`, payload).then(r => r.data),
    onSuccess: (data: { type: string; amount_fils: number }) => {
      setRefundResult(data)
      setRefundError(null)
      setRefundModalOpen(false)
      qc.invalidateQueries({ queryKey: ['order-detail', orderId] })
      qc.invalidateQueries({ queryKey: ['orders'] })
    },
    onError: (e: { response?: { data?: { message?: string } } }) => {
      setRefundError(e.response?.data?.message ?? 'Refund failed')
    },
  })

  // POS void (slice 2b) — distinct from delivery refund: restocks the
  // products, reverses drops + loyalty + store credit, decrements the
  // discount code's usage counter, audit-logged as `pos_sale.void`. The
  // modal forces the admin to pick a reason from a fixed chip set; "Other"
  // requires a free-text explanation.
  const [voidModalOpen, setVoidModalOpen] = useState(false)
  const [voidError, setVoidError] = useState<string | null>(null)
  const voidPos = useMutation({
    mutationFn: (payload: { reason_code: string; reason_text?: string }) =>
      api.post(`/orders/${orderId}/void-pos`, payload).then(r => r.data),
    onSuccess: () => {
      setVoidModalOpen(false)
      setVoidError(null)
      qc.invalidateQueries({ queryKey: ['order-detail', orderId] })
      qc.invalidateQueries({ queryKey: ['orders'] })
    },
    onError: (e: { response?: { data?: { message?: string } } }) => {
      setVoidError(e.response?.data?.message ?? 'Void failed')
    },
  })

  const { data, isLoading } = useQuery<OrderDetail>({
    queryKey: ['order-detail', orderId],
    queryFn: () => api.get(`/orders/${orderId}`).then(r => r.data),
  })

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[#111] border-l border-[#2e2e2e] h-full overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2e2e2e] sticky top-0 bg-[#111] z-10">
          <div>
            <p className="text-white font-semibold">{data?.order.order_number ?? '…'}</p>
            {data && <p className="text-gray-500 text-xs mt-0.5">{formatDateTime(data.order.created_at)}</p>}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors p-1">
            <X size={18} />
          </button>
        </div>

        {isLoading && (
          <div className="flex-1 flex items-center justify-center text-gray-600 text-sm">Loading…</div>
        )}

        {data && (
          <div className="p-5 space-y-5">
            {/* Items */}
            <section>
              <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Items</p>
              <div className="space-y-2">
                {data.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#1a1a1a] rounded-lg px-3 py-2.5">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xs text-gray-600 w-5 text-right shrink-0">{item.quantity}×</span>
                      <div className="min-w-0">
                        <p className="text-white text-sm truncate">{item.name}</p>
                        {item.name_ar && <p className="text-gray-600 text-xs truncate">{item.name_ar}</p>}
                        {(item.gift_wrap === 1 || item.gift_wrap === true) && (
                          <div className="mt-0.5">
                            <span className="text-[#c41e3a] text-xs font-medium">🎁 Gift Wrapped</span>
                            {item.gift_note && <p className="text-gray-500 text-xs italic mt-0.5">"{item.gift_note}"</p>}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <p className="text-gray-300 text-sm">{formatCurrency(item.quantity * item.unit_price_fils)}</p>
                      <p className="text-gray-600 text-xs">{formatCurrency(item.unit_price_fils)} each</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Extras / Addons */}
            {data.addons?.length > 0 && (
              <section>
                <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Extras</p>
                <div className="space-y-2">
                  {data.addons.map((addon, i) => (
                    <div key={i} className="flex items-center justify-between bg-[#1a1a1a] rounded-lg px-3 py-2.5">
                      <div className="flex items-center gap-3 min-w-0">
                        {addon.quantity > 1 && (
                          <span className="text-xs text-gray-600 w-5 text-right shrink-0">{addon.quantity}×</span>
                        )}
                        <div className="min-w-0">
                          <p className="text-white text-sm truncate">{addon.name_en}</p>
                          {addon.name_ar && <p className="text-gray-600 text-xs truncate">{addon.name_ar}</p>}
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        {addon.unit_price === 0
                          ? <p className="text-green-400 text-sm font-medium">Free</p>
                          : <p className="text-gray-300 text-sm">{formatCurrency(addon.total)}</p>
                        }
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Prize items */}
            {data.prizeItems?.length > 0 && (
              <section>
                <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Prize Redemptions</p>
                <div className="space-y-2">
                  {data.prizeItems.map((p) => (
                    <div key={p.play_id} className="flex items-center justify-between bg-[#1a1a1a] rounded-lg px-3 py-2.5">
                      <div className="min-w-0">
                        <p className="text-white text-sm">🎁 {p.name_en}</p>
                        <p className="text-gray-500 text-xs">{p.game_name} · {p.prize_type.replace(/_/g, ' ')}</p>
                      </div>
                      <span className="text-green-400 text-sm font-medium shrink-0 ml-3">Free</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Totals */}
            <section className="bg-[#1a1a1a] rounded-lg px-4 py-3 space-y-2 text-sm">
              <div className="flex justify-between text-gray-400">
                <span>Subtotal</span>
                <span>{formatCurrency(data.order.subtotal_fils ?? (data.order.total_fils - (data.order.delivery_fee_fils ?? 0)))}</span>
              </div>
              <div className="flex justify-between text-gray-400">
                <span>Delivery fee</span>
                <span>{formatCurrency(data.order.delivery_fee_fils ?? 0)}</span>
              </div>
              {(data.order.discount_amount_fils ?? 0) > 0 && (
                <div className="flex justify-between text-green-400">
                  <span>
                    Discount
                    {data.order.discount_code && (
                      <span className="ml-1.5 font-mono text-xs bg-green-400/10 px-1.5 py-0.5 rounded">{data.order.discount_code}</span>
                    )}
                  </span>
                  <span>−{formatCurrency(data.order.discount_amount_fils)}</span>
                </div>
              )}
              {(data.applied_bundles ?? []).map((b) => (
                <div key={b.id} className="flex justify-between text-green-400">
                  <span>
                    Bundle
                    <span className="ml-1.5 text-xs bg-green-400/10 px-1.5 py-0.5 rounded">{b.name_en}</span>
                  </span>
                  <span>−{formatCurrency(b.discount_fils)}</span>
                </div>
              ))}
              {(data.order.tip_fils ?? 0) > 0 && (
                <div className="flex justify-between text-[#C41E3A]">
                  <span>Ambassador tip</span>
                  <span>{formatCurrency(data.order.tip_fils!)}</span>
                </div>
              )}
              {(data.order.store_credit_applied_fils ?? 0) > 0 && (
                // Customer's wallet credit applied at checkout — already
                // baked into the total (the line just makes it explicit so
                // ops can tell when a customer paid in part with credit).
                <div className="flex justify-between text-blue-300">
                  <span>Store credit applied</span>
                  <span>−{formatCurrency(data.order.store_credit_applied_fils!)}</span>
                </div>
              )}
              {(data.order.gift_card_applied_fils ?? 0) > 0 && (
                // Bearer gift card balance applied. Like store credit —
                // already baked into the total; surface explicitly so
                // finance can split gift-card tender from cash / card.
                <div className="flex justify-between text-emerald-300">
                  <span>Gift card applied</span>
                  <span>−{formatCurrency(data.order.gift_card_applied_fils!)}</span>
                </div>
              )}
              <div className="flex justify-between text-white font-semibold border-t border-[#2e2e2e] pt-2 mt-1">
                <span>Total</span>
                <span>{formatCurrency(data.order.total_fils)}</span>
              </div>
              {/* Ops-side margin block (2026-07-12) — COGS is snapshotted
                  per-line on stock_movements at ring-up, so this survives
                  future WAC changes. Margin base is subtotal (product
                  economics), not total (which is muddied by discounts /
                  delivery fees / wallet tender). Suppressed when COGS is
                  zero — usually means the product had no PO receive yet
                  and displaying a fake 100% margin would mislead ops. */}
              {(data.order.cogs_fils ?? 0) > 0 && (
                <div className="border-t border-[#2e2e2e] pt-2 mt-1 space-y-1 text-xs">
                  <div className="flex justify-between text-gray-500">
                    <span>COGS</span>
                    <span>{formatCurrency(data.order.cogs_fils!)}</span>
                  </div>
                  <div className="flex justify-between text-gray-300 font-medium">
                    <span>Gross margin</span>
                    <span className={(data.order.margin_fils ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                      {formatCurrency(data.order.margin_fils!)}
                      {data.order.margin_pct != null && (
                        <span className="ml-1.5 text-gray-500">({data.order.margin_pct.toFixed(1)}%)</span>
                      )}
                    </span>
                  </div>
                </div>
              )}
            </section>

            {/* Fulfillment info */}
            <section>
              <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">
                {data.order.channel === 'pos'
                  ? 'In-store'
                  : data.order.fulfillment === 'pickup' ? 'Pickup' : 'Delivery'}
              </p>
              <div className="bg-[#1a1a1a] rounded-lg px-4 py-3 space-y-1.5 text-sm">
                {data.order.channel === 'pos' && (
                  <div className="flex gap-2 items-center">
                    <span className="text-gray-600 w-20 shrink-0">Type</span>
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 text-xs font-semibold">
                      🛍 In-store
                    </span>
                  </div>
                )}
                {data.order.channel !== 'pos' && data.order.fulfillment === 'pickup' && (
                  <div className="flex gap-2 items-center">
                    <span className="text-gray-600 w-20 shrink-0">Type</span>
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400 text-xs font-semibold">
                      🏪 Store Pickup
                    </span>
                  </div>
                )}
                {data.order.channel === 'delivery' && data.order.cashier_id && (
                  <div className="flex gap-2 items-center">
                    <span className="text-gray-600 w-20 shrink-0">Type</span>
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300 text-xs font-semibold">
                      📞 Phone-in
                    </span>
                    {data.order.cashier_name && (
                      <span className="text-gray-500 text-xs">by {data.order.cashier_name}</span>
                    )}
                  </div>
                )}
                {data.order.scheduled_for && (
                  <div className="flex gap-2 items-center">
                    <span className="text-gray-600 w-20 shrink-0">Scheduled</span>
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 text-xs font-semibold">
                      📅 {formatScheduledWindow(data.order.scheduled_for, data.order.scheduled_window_minutes ?? 60)}
                    </span>
                  </div>
                )}
                <div className="flex gap-2">
                  <span className="text-gray-600 w-20 shrink-0">Name</span>
                  <span className="text-white">{data.order.delivery_name || data.order.full_name}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-gray-600 w-20 shrink-0">Phone</span>
                  <span className="text-white">{data.order.delivery_phone}</span>
                </div>
                {data.order.fulfillment !== 'pickup' && data.order.delivery_address && (
                  <div className="flex gap-2">
                    <span className="text-gray-600 w-20 shrink-0">Address</span>
                    <span className="text-white">{data.order.delivery_address}</span>
                  </div>
                )}
                <div className="flex gap-2">
                  <span className="text-gray-600 w-20 shrink-0">Payment</span>
                  <span className="text-white capitalize">{(data.order.effective_payment_method ?? data.order.payment_method)?.replace(/_/g, ' ')}</span>
                  {data.order.payment_status && data.order.payment_method === 'card' && (
                    <PaymentStatusBadge
                      status={data.order.payment_status}
                      authPending={!!data.order.card_authorization_id}
                      lastAction={data.order.payment_last_action}
                    />
                  )}
                </div>
                {data.order.payment_method === 'card' && canRefund && data.order.payment_status !== 'refunded' && (
                  <div className="pt-2">
                    {refundResult ? (
                      <div className="rounded-md border border-green-500/30 bg-green-500/10 px-3 py-2 text-xs text-green-300">
                        {refundResult.type === 'void' ? 'Authorization voided' : 'Refund issued'} —{' '}
                        {formatCurrency(refundResult.amount_fils)}
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => { setRefundError(null); setRefundModalOpen(true) }}
                          disabled={refund.isPending}
                          className="inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-white/10 disabled:opacity-50"
                        >
                          <RotateCcw size={12} />
                          {refund.isPending
                            ? '…'
                            : data.order.card_authorization_id ? 'Void authorization' : 'Refund to card'}
                        </button>
                        {refundError && (
                          <p className="mt-1.5 text-xs text-red-400">{refundError}</p>
                        )}
                      </>
                    )}
                  </div>
                )}
                {data.order.payment_status === 'refunded' && data.order.refund_reason_code && (
                  <div className="pt-2 rounded-md border border-blue-500/20 bg-blue-500/5 px-3 py-2 text-xs">
                    <p className="text-gray-500">
                      Refund reason —{' '}
                      <span className="text-blue-200">
                        {REFUND_REASON_LABELS[data.order.refund_reason_code] ?? data.order.refund_reason_code}
                      </span>
                      {data.order.refund_reason && data.order.refund_reason_code === 'other' && (
                        <span className="text-blue-200"> · {data.order.refund_reason}</span>
                      )}
                    </p>
                  </div>
                )}
                {/* JoFotara triage — delivery orders submit at the
                    status='delivered' transition (2026-07-10 delivered
                    hook). NULL status means the transition hasn't
                    fired yet; card hides itself in that case. */}
                {data.order.channel === 'delivery' && (
                  <>
                    <JofotaraTriageCard
                      kind="sale"
                      orderId={orderId}
                      status={(data.order.jofotara_status ?? null) as JofotaraStatus}
                      invoiceId={data.order.jofotara_invoice_id ?? null}
                      uuid={data.order.jofotara_uuid ?? null}
                      submittedAt={data.order.jofotara_submitted_at ?? null}
                      attempts={data.order.jofotara_attempts ?? null}
                      responseJson={data.order.jofotara_response_json ?? null}
                      invalidateKeys={[['orders'], ['order-detail', orderId]]}
                    />
                    {data.order.jofotara_void_status && (
                      <JofotaraTriageCard
                        kind="void"
                        orderId={orderId}
                        status={data.order.jofotara_void_status as JofotaraStatus}
                        invoiceId={data.order.jofotara_void_invoice_id ?? null}
                        uuid={data.order.jofotara_void_uuid ?? null}
                        submittedAt={data.order.jofotara_void_submitted_at ?? null}
                        attempts={data.order.jofotara_void_attempts ?? null}
                        responseJson={data.order.jofotara_void_response_json ?? null}
                        invalidateKeys={[['orders'], ['order-detail', orderId]]}
                      />
                    )}
                  </>
                )}
                {data.order.channel === 'pos' && data.returns && data.returns.length > 0 && (
                  <div className="pt-2 rounded-md border border-blue-500/30 bg-blue-500/5 px-3 py-2 text-xs space-y-2">
                    <div className="font-semibold flex items-center gap-1.5 text-blue-300">
                      <Undo2 size={12} />
                      Returns ({data.returns.length})
                    </div>
                    {data.returns.map((r) => (
                      <div key={r.id} className="border-t border-blue-500/15 pt-2 space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-blue-200 font-mono">{r.return_number}</span>
                          <span className="text-blue-200 font-semibold">−{formatCurrency(r.total_refund_fils)}</span>
                        </div>
                        <div className="text-gray-400">
                          {r.refund_method === 'cash'
                            ? 'Cash from drawer'
                            : r.refund_method === 'card'
                              ? 'Refund to card (terminal)'
                              : 'Store credit'}
                          {r.reason_code && (
                            <> · <span className="capitalize">{(REASON_LABELS[r.reason_code] ?? r.reason_code).toLowerCase()}</span></>
                          )}
                          {r.reason_code === 'other' && r.reason && (
                            <span className="text-gray-500"> · {r.reason}</span>
                          )}
                        </div>
                        {r.lines.length > 0 && (
                          <ul className="text-gray-400 space-y-0.5 pl-3">
                            {r.lines.map((l, i) => (
                              <li key={i} className="flex items-baseline justify-between gap-2">
                                <span className="truncate">
                                  {l.quantity}× {l.product_name ?? '(item)'}
                                  {l.restocked === 0 && (
                                    <span className="ml-1.5 text-amber-400/80 text-[10px]">(not restocked)</span>
                                  )}
                                </span>
                                <span className="text-gray-500 shrink-0">−{formatCurrency(l.refund_line_fils)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="text-gray-500 text-[11px]">
                          By {r.cashier_name ?? 'cashier'}
                          {r.authorised_by_admin_email && <> · Auth {r.authorised_by_admin_email}</>}
                          {' · '}{formatDateTime(r.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {data.order.channel === 'pos' && data.order.voided_at && (
                  <div className="pt-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300 space-y-1">
                    <div className="font-semibold flex items-center gap-1.5">
                      <RotateCcw size={12} />
                      Sale voided
                    </div>
                    <div>
                      Reason — <span className="text-red-200">{VOID_REASON_LABELS[data.order.void_reason_code ?? ''] ?? data.order.void_reason_code}</span>
                      {data.order.void_reason && data.order.void_reason_code === 'other' && (
                        <span className="text-red-200"> · {data.order.void_reason}</span>
                      )}
                    </div>
                    <div className="text-red-400/80">
                      By {data.order.voided_by_admin_email ?? 'admin'} · {formatDateTime(data.order.voided_at)}
                    </div>
                  </div>
                )}
                {data.order.channel === 'pos' && canRefund && !data.order.voided_at && data.order.status !== 'cancelled' && (
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => { setVoidError(null); setVoidModalOpen(true) }}
                      className="inline-flex items-center gap-1.5 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-300 transition-colors hover:bg-red-500/20"
                    >
                      <RotateCcw size={12} />
                      Void this sale
                    </button>
                    {voidError && (
                      <p className="mt-1.5 text-xs text-red-400">{voidError}</p>
                    )}
                  </div>
                )}
                {(data.order.status === 'delivered' || data.order.channel === 'pos') && (
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => setSendReceiptOpen(true)}
                      className="inline-flex items-center gap-1.5 rounded-md border border-[#3a3a3a] bg-[#2a2a2a] px-3 py-1.5 text-xs font-medium text-gray-200 transition-colors hover:bg-[#333]"
                      title="Send the receipt link over SMS or email"
                    >
                      <Send size={12} />
                      Send receipt
                    </button>
                  </div>
                )}
                {data.order.notes && (
                  <div className="flex gap-2">
                    <span className="text-gray-600 w-20 shrink-0">Notes</span>
                    <span className="text-yellow-400">{data.order.notes}</span>
                  </div>
                )}
              </div>
            </section>
          </div>
        )}
        {voidModalOpen && (
          <PosVoidModal
            orderNumber={data?.order.order_number ?? ''}
            isSubmitting={voidPos.isPending}
            errorMessage={voidError}
            onCancel={() => setVoidModalOpen(false)}
            onConfirm={(code, text) => voidPos.mutate({ reason_code: code, reason_text: text })}
          />
        )}
        {refundModalOpen && data && (
          <RefundReasonModal
            orderNumber={data.order.order_number}
            amountFils={data.order.total_fils}
            action={data.order.card_authorization_id ? 'void' : 'refund'}
            isSubmitting={refund.isPending}
            errorMessage={refundError}
            onCancel={() => setRefundModalOpen(false)}
            onConfirm={(code, text) => refund.mutate({ reason_code: code, reason_text: text })}
          />
        )}
        {sendReceiptOpen && data && (
          <SendReceiptModal
            orderId={data.order.id}
            orderNumber={data.order.order_number}
            onClose={() => setSendReceiptOpen(false)}
          />
        )}
      </div>
    </div>
  )
}

export default function Orders() {
  const [statusFilter, setStatusFilter] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [sortCol, setSortCol] = useState('created_at')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const qc = useQueryClient()
  // Deep-link support — admin pages can link here as `/orders?id=<orderId>`
  // (the dashboard does this for pending / recent rows). On mount and
  // on every param change, sync the URL into local state so the
  // OrderDetailPanel opens automatically. Closing the panel clears the
  // param so the URL goes back to a plain `/orders`.
  const [searchParams, setSearchParams] = useSearchParams()
  const urlOrderId = searchParams.get('id')
  useEffect(() => {
    if (urlOrderId && urlOrderId !== selectedId) setSelectedId(urlOrderId)
  }, [urlOrderId, selectedId])

  function handleSort(col: string) {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortCol(col)
      setSortDir('asc')
    }
    setPage(1)
  }

  const params = new URLSearchParams()
  if (statusFilter) params.set('status', statusFilter)
  if (search) params.set('search', search)
  params.set('sort', sortCol)
  params.set('order', sortDir)
  params.set('page', String(page))

  const { data, isError } = useQuery<{ orders: Order[]; total: number; limit: number }>({
    queryKey: ['orders', statusFilter, search, page, sortCol, sortDir],
    queryFn: () => api.get(`/orders?${params}`).then(r => r.data),
  })

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.patch(`/orders/${id}/status`, { status }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['orders'] })
      // GA4: track the to-status so we can see admin's flip patterns
      // (e.g. how many orders are auto-cancelled vs flipped to delivered
      // manually from the admin side). Tracked here on success only so
      // failed mutations don't pollute the funnel.
      trackEvent('admin_order_status_change', { to: vars.status })
    },
  })

  const totalPages = Math.ceil((data?.total ?? 0) / (data?.limit ?? 50))

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-lg font-semibold text-white">Orders</h1>
        <CsvControls
          basePath="/orders"
          exportFilename={`orders-export-${new Date().toISOString().slice(0, 10)}.csv`}
          exportOnly
        />
      </div>
      {isError && <div className="mb-4 text-red-400 text-sm bg-red-400/10 rounded-lg px-4 py-3">Failed to load orders. Check your connection or try refreshing.</div>}

      {/* Filters */}
      <div className="flex gap-3 mb-5">
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1) }}
          placeholder="Search order #, name, phone…"
          className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A] w-64"
        />
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1) }}
          className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
        >
          <option value="">All statuses</option>
          {STATUSES.slice(1).map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
      </div>

      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-600 border-b border-[#2e2e2e]">
              <th className="px-4 py-3 w-6" />
              <SortTh label="Order"    col="order_number" current={sortCol} dir={sortDir} onSort={handleSort} />
              <th className="px-4 py-3 text-left font-medium">Customer</th>
              <th className="px-4 py-3 text-left font-medium">Address</th>
              <SortTh label="Total"    col="total"        current={sortCol} dir={sortDir} onSort={handleSort} />
              <th className="px-4 py-3 text-left font-medium">Payment</th>
              <SortTh label="Status"   col="status"       current={sortCol} dir={sortDir} onSort={handleSort} />
              <SortTh label="Date"     col="created_at"   current={sortCol} dir={sortDir} onSort={handleSort} />
            </tr>
          </thead>
          <tbody>
            {data?.orders.map((o) => (
              <tr
                key={o.id}
                className="border-b border-[#222] hover:bg-white/2 transition-colors cursor-pointer"
                onClick={() => setSelectedId(o.id)}
              >
                <td className="pl-4 pr-0 py-3 text-gray-600">
                  <ChevronRight size={14} />
                </td>
                <td className="px-4 py-3 font-mono text-xs text-gray-300">{o.order_number}</td>
                <td className="px-4 py-3">
                  <p className="text-white">{o.delivery_name || o.full_name}</p>
                  <p className="text-gray-500 text-xs">{o.delivery_phone || o.phone}</p>
                </td>
                <td className="px-4 py-3 text-gray-400 text-xs max-w-[200px]">
                  {o.channel === 'pos' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 font-semibold whitespace-nowrap">🛍 In-store</span>
                  ) : o.fulfillment === 'pickup' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400 font-semibold whitespace-nowrap">🏪 Store Pickup</span>
                  ) : (
                    <div className="flex flex-wrap gap-1 items-center">
                      {o.cashier_id && (
                        // Phone-in delivery — a cashier took the order
                        // verbally over the phone and rang it through
                        // the till. See FEATURE_LOG → POS App → Phone-in.
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300 font-semibold whitespace-nowrap">📞 Phone-in</span>
                      )}
                      {o.scheduled_for && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 font-semibold whitespace-nowrap"
                              title={`Driver should arrive within the ${o.scheduled_window_minutes ?? 60}-min window`}>
                          📅 {formatScheduledWindow(o.scheduled_for, o.scheduled_window_minutes ?? 60)}
                        </span>
                      )}
                      <span className="truncate text-gray-400">{o.delivery_address}</span>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-300 whitespace-nowrap">{formatCurrency(o.total_fils)}</td>
                <td className="px-4 py-3 text-gray-400 text-xs capitalize">{(o.effective_payment_method ?? o.payment_method)?.replace(/_/g, ' ')}</td>
                <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                  {o.channel === 'pos' ? (
                    // POS sale — terminal at completion or void. Voiding is
                    // a separate action (slice 2b), not a status flip. Partial
                    // returns (migration 0113) flag `partially_refunded_at`;
                    // once cumulative refund hits the order total the worker
                    // also flips payment_status to 'refunded'.
                    o.status === 'cancelled' ? (
                      <span className="inline-block text-xs px-2 py-0.5 rounded-full font-medium bg-red-500/15 text-red-400">
                        Voided
                      </span>
                    ) : o.payment_status === 'refunded' && o.partially_refunded_at ? (
                      <span className="inline-block text-xs px-2 py-0.5 rounded-full font-medium bg-blue-500/15 text-blue-300">
                        Refunded
                      </span>
                    ) : o.partially_refunded_at ? (
                      <span className="inline-block text-xs px-2 py-0.5 rounded-full font-medium bg-amber-500/15 text-amber-300">
                        Partial refund
                      </span>
                    ) : (
                      <span className="inline-block text-xs px-2 py-0.5 rounded-full font-medium bg-green-500/15 text-green-400">
                        Completed
                      </span>
                    )
                  ) : o.returned_to_store_at ? (
                    // Returned-to-store is terminal and was set by the
                    // driver-side endpoint; admin can't (and shouldn't)
                    // flip it through this dropdown. Render as a plain
                    // read-only chip.
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS.returned_to_store}`}>
                      {STATUS_LABELS.returned_to_store}
                    </span>
                  ) : (
                    <StatusSelect
                      value={o.status}
                      onChange={(s) => updateStatus.mutate({ id: o.id, status: s })}
                    />
                  )}
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{formatDateTime(o.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#2e2e2e] text-xs text-gray-500">
            <span>{data?.total} total orders</span>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 rounded bg-[#2e2e2e] disabled:opacity-40 hover:bg-[#3a3a3a] transition-colors">Prev</button>
              <span className="px-2 py-1">Page {page} / {totalPages}</span>
              <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 rounded bg-[#2e2e2e] disabled:opacity-40 hover:bg-[#3a3a3a] transition-colors">Next</button>
            </div>
          </div>
        )}
      </div>

      {selectedId && (
        <OrderDetailPanel orderId={selectedId} onClose={() => {
          setSelectedId(null)
          if (searchParams.has('id')) {
            const next = new URLSearchParams(searchParams)
            next.delete('id')
            setSearchParams(next, { replace: true })
          }
        }} />
      )}
    </div>
  )
}

function StatusSelect({ value, onChange }: { value: string; onChange: (s: string) => void }) {
  return (
    <div className="relative inline-flex items-center">
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className={`appearance-none text-xs px-2 py-0.5 pr-5 rounded-full font-medium focus:outline-none cursor-pointer ${STATUS_COLORS[value] ?? 'bg-gray-500/15 text-gray-400'}`}
      >
        {Object.entries(STATUS_LABELS).map(([k, v]) => (
          <option key={k} value={k} className="bg-[#1a1a1a] text-white">{v}</option>
        ))}
      </select>
      <ChevronDown size={10} className="absolute right-1.5 pointer-events-none opacity-60" />
    </div>
  )
}

function SortTh({ label, col, current, dir, onSort }: {
  label: string; col: string; current: string; dir: 'asc' | 'desc'; onSort: (c: string) => void
}) {
  const active = current === col
  return (
    <th className="px-4 py-3 text-left">
      <button onClick={() => onSort(col)} className="flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-white transition-colors group">
        {label}
        <span className={active ? 'text-[#C41E3A]' : 'text-gray-700 group-hover:text-gray-500'}>
          {active ? (dir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </button>
    </th>
  )
}

// Modal shown when an admin clicks "Void this sale" on a POS order detail.
// Five preset reason chips + a free-text input that becomes required when
// "Other" is selected. Submits to `POST /admin/orders/:id/void-pos` —
// server-side does the restock + ledger reversal + audit log.
// PosVoidModal moved to `admin/src/components/PosVoidModal.tsx` so the
// dedicated Sales (POS) page can reuse it without duplicating the form
// state + reason chips.
